import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-2xl border border-sand-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-redline-500/30";

// Where an invite / "set your password" link lands: the link proves who they are, they type a
// password, and they're in. Nothing happens until a password is typed, so a scanner can't burn it.
export default function PortalActivate() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(token ? "" : "This link is missing its code — open it from your message again.");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/resident/auth/activate/peek`, { params: { token } })
      .then((r) => setInfo(r.data))
      .catch((e) => { const d = e?.response?.data?.detail; setErr((d && d.message) || "This link isn't valid any more."); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setErr("Use at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Those passwords don't match."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/resident/auth/activate/link`, { token, password: pw }, { withCredentials: true });
      toast.success(`Welcome${data.name ? `, ${data.name.split(" ")[0]}` : ""}! You're signed in.`);
      navigate("/portal", { replace: true });
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : d?.message || "Couldn't set your password.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[#1a1614] flex items-center justify-center px-4 py-8">
      <form onSubmit={submit} className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full" data-testid="portal-activate-page">
        <div className="mx-auto h-12 w-12 rounded-2xl bg-redline-50 flex items-center justify-center mb-4">
          <KeyRound className="h-6 w-6 text-redline-500" strokeWidth={1.75} />
        </div>
        <h1 className="font-heading font-bold text-xl text-ink text-center">
          {info ? (info.has_password ? "Reset your password" : `Welcome${info.first_name ? `, ${info.first_name}` : ""}`) : "Set your password"}
        </h1>
        <p className="text-sm text-ink/60 mt-2 text-center">
          {info ? <>Pick the password you'll use for the <span className="font-semibold text-ink">{info.community}</span> resident portal. From then on you sign in with your mobile or email and this password, on any device.</>
                : "Checking your link…"}
        </p>
        <input data-testid="portal-activate-password" type="password" autoFocus autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (8+ characters)" className={`${inputCls} mt-6`} disabled={!info} />
        <input data-testid="portal-activate-confirm" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Type it again" className={`${inputCls} mt-3`} disabled={!info} />
        {err && <p className="mt-3 text-sm text-danger-700 flex items-start gap-1.5" data-testid="portal-activate-error"><ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" /> {err}</p>}
        <button data-testid="portal-activate-submit" type="submit" disabled={busy || !info}
          className="mt-5 w-full rounded-full bg-ink text-white font-bold py-3.5 hover:bg-ink/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Save password and sign in"}
        </button>
        <button type="button" onClick={() => navigate("/portal/login")} className="block w-full text-center mt-4 text-sm font-semibold text-redline-600">Sign in another way</button>
      </form>
    </div>
  );
}
